import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User, Users, UserPlus, Settings, CheckCircle
} from 'lucide-react';
import { theme } from '../../theme';
import { API_BASE_URL, isGuestMode } from '../../config/api';

const MOCK_FOLLOWERS = [
  { username: 'BudgetBrawler', status: 'accepted', created_at: '2026-03-01' },
  { username: 'PennyPuncher', status: 'accepted', created_at: '2026-03-10' },
  { username: 'CashCrusher', status: 'pending', created_at: '2026-03-20' },
];

const MOCK_FOLLOWING = [
  { username: 'DebtDestroyer', status: 'accepted', created_at: '2026-02-15' },
  { username: 'SavageSaver', status: 'accepted', created_at: '2026-03-05' },
];

const MOCK_CATEGORIES = ['Housing', 'Groceries', 'Transport', 'Healthcare', 'Debt Payments'];

interface ArenaProfileProps {
  user: {
    id: number;
    username: string;
    email: string;
  };
}

interface FollowUser {
  username: string;
  status: string;
  created_at: string;
}

const ESSENTIAL_CATEGORIES = [
  'Housing',
  'Groceries',
  'Transport',
  'Utilities',
  'Healthcare',
  'Childcare',
  'Debt Payments',
  'Insurance',
  'Work Expenses',
  'Education',
];

export function ArenaProfile({ user }: ArenaProfileProps) {
  const [followers, setFollowers] = useState<FollowUser[]>([]);
  const [following, setFollowing] = useState<FollowUser[]>([]);
  const [essentialCategories, setEssentialCategories] = useState<string[]>([]);
  const [newFollowUsername, setNewFollowUsername] = useState('');
  const [activeTab, setActiveTab] = useState<'settings' | 'social'>('settings');
  const [loading, setLoading] = useState(true);
  const [savingCategories, setSavingCategories] = useState(false);

  const guest = isGuestMode();

  useEffect(() => {
    if (guest) {
      setFollowers(MOCK_FOLLOWERS);
      setFollowing(MOCK_FOLLOWING);
      setEssentialCategories(MOCK_CATEGORIES);
      setLoading(false);
      return;
    }
    fetchSocialData();
    fetchUserSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSocialData = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const [followersRes, followingRes] = await Promise.all([
        fetch(`${API_BASE_URL}/arena/social/followers`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/arena/social/following`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (followersRes.ok) {
        const data = await followersRes.json();
        setFollowers(data.followers);
      }

      if (followingRes.ok) {
        const data = await followingRes.json();
        setFollowing(data.following);
      }
    } catch (err) {
      console.error('Failed to fetch social data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSettings = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/arena/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const categories = JSON.parse(data.user.essential_categories || '[]');
        setEssentialCategories(categories);
      }
    } catch (err) {
      console.error('Failed to fetch user settings:', err);
    }
  };

  const handleFollowUser = async () => {
    if (!newFollowUsername.trim()) return;
    if (guest) return;

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/arena/social/follow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: newFollowUsername }),
      });

      if (response.ok) {
        setNewFollowUsername('');
        fetchSocialData();
      }
    } catch (err) {
      console.error('Failed to follow user:', err);
    }
  };

  const handleAcceptFollow = async (followerUsername: string) => {
    if (guest) return;
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/arena/social/accept-follow/${followerUsername}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        fetchSocialData();
      }
    } catch (err) {
      console.error('Failed to accept follow:', err);
    }
  };

  const toggleCategory = (category: string) => {
    if (essentialCategories.includes(category)) {
      setEssentialCategories(essentialCategories.filter(c => c !== category));
    } else {
      setEssentialCategories([...essentialCategories, category]);
    }
  };

  const saveEssentialCategories = async () => {
    if (guest) return;
    setSavingCategories(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/arena/profile/essential-categories`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ categories: essentialCategories }),
      });

      if (response.ok) {
        // Show success feedback
        setTimeout(() => setSavingCategories(false), 1000);
      }
    } catch (err) {
      console.error('Failed to save categories:', err);
      setSavingCategories(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: theme.spacing[8],
        color: theme.semantic.text.secondary,
      }}>
        Loading profile...
      </div>
    );
  }

  return (
    <div>
      {/* Profile Header */}
      <div style={{
        background: theme.semantic.bg.surface,
        border: `1px solid ${theme.semantic.border.default}`,
        borderRadius: theme.radii.md,
        padding: theme.spacing[6],
        marginBottom: theme.spacing[6],
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing[4],
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: `linear-gradient(135deg, ${theme.brand.crimson}, ${theme.brand.copper})`,
            borderRadius: theme.radii.full,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <User size={40} color={theme.semantic.text.inverse} />
          </div>

          <div>
            <h2 style={{
              fontFamily: theme.typography.fontFamily.sans,
              fontSize: theme.typography.fontSize.xl,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              margin: 0,
            }}>
              {user.username}
            </h2>
            <div style={{
              display: 'flex',
              gap: theme.spacing[4],
              marginTop: theme.spacing[2],
              fontSize: theme.typography.fontSize.sm,
              color: theme.semantic.text.secondary,
            }}>
              <span>{followers.filter(f => f.status === 'accepted').length} Followers</span>
              <span>{following.filter(f => f.status === 'accepted').length} Following</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: theme.spacing[2],
        marginBottom: theme.spacing[4],
      }}>
        <button
          onClick={() => setActiveTab('settings')}
          style={{
            background: activeTab === 'settings' ? theme.semantic.action.primary : 'transparent',
            color: activeTab === 'settings' ? theme.semantic.text.primary : theme.semantic.text.secondary,
            border: `2px solid ${activeTab === 'settings' ? theme.semantic.border.strong : theme.semantic.border.default}`,
            padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
            borderRadius: theme.radii.md,
            fontFamily: theme.typography.fontFamily.sans,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
          }}
        >
          <Settings size={16} />
          Settings
        </button>

        <button
          onClick={() => setActiveTab('social')}
          style={{
            background: activeTab === 'social' ? theme.semantic.action.primary : 'transparent',
            color: activeTab === 'social' ? theme.semantic.text.primary : theme.semantic.text.secondary,
            border: `2px solid ${activeTab === 'social' ? theme.semantic.border.strong : theme.semantic.border.default}`,
            padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
            borderRadius: theme.radii.md,
            fontFamily: theme.typography.fontFamily.sans,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
          }}
        >
          <Users size={16} />
          Social
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Essential Categories */}
          <div style={{
            background: theme.semantic.bg.surface,
            border: `1px solid ${theme.semantic.border.default}`,
            borderRadius: theme.radii.md,
            padding: theme.spacing[6],
            marginBottom: theme.spacing[4],
          }}>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.sans,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              margin: 0,
              marginBottom: theme.spacing[2],
            }}>
              Essential Spending Categories
            </h3>
            <p style={{
              color: theme.semantic.text.secondary,
              fontSize: theme.typography.fontSize.sm,
              marginBottom: theme.spacing[4],
            }}>
              Select what counts as essential for your no-spend days
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))',
              gap: theme.spacing[3],
              marginBottom: theme.spacing[4],
              overflow: 'hidden',
            }}>
              {ESSENTIAL_CATEGORIES.map((category) => {
                const isSelected = essentialCategories.includes(category);
                return (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    style={{
                      background: isSelected ? `${theme.brand.crimson}22` : theme.brand.charcoal,
                      border: `1px solid ${isSelected ? theme.brand.crimson : theme.semantic.border.default}`,
                      borderRadius: theme.radii.md,
                      padding: theme.spacing[3],
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: theme.spacing[2],
                    }}
                  >
                    <span style={{
                      color: isSelected ? theme.brand.crimson : theme.semantic.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                    }}>
                      {category}
                    </span>
                    {isSelected && <CheckCircle size={16} color={theme.brand.crimson} />}
                  </button>
                );
              })}
            </div>

            <button
              onClick={saveEssentialCategories}
              disabled={savingCategories}
              style={{
                background: theme.brand.crimson,
                color: theme.semantic.text.inverse,
                border: 'none',
                borderRadius: theme.radii.md,
                padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
                fontFamily: theme.typography.fontFamily.sans,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.bold,
                cursor: savingCategories ? 'not-allowed' : 'pointer',
                opacity: savingCategories ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {savingCategories ? 'Saving...' : 'Save Categories'}
            </button>
          </div>
        </motion.div>
      )}

      {/* Social Tab */}
      {activeTab === 'social' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Follow New User */}
          <div style={{
            background: theme.semantic.bg.surface,
            border: `1px solid ${theme.semantic.border.default}`,
            borderRadius: theme.radii.md,
            padding: theme.spacing[5],
            marginBottom: theme.spacing[4],
          }}>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.sans,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              margin: 0,
              marginBottom: theme.spacing[3],
            }}>
              Follow a Warrior
            </h3>

            <div style={{
              display: 'flex',
              gap: theme.spacing[2],
              flexWrap: 'wrap',
            }}>
              <input
                type="text"
                value={newFollowUsername}
                onChange={(e) => setNewFollowUsername(e.target.value)}
                placeholder="Enter username"
                style={{
                  flex: 1,
                  padding: theme.spacing[3],
                  background: theme.brand.charcoal,
                  border: `1px solid ${theme.semantic.border.default}`,
                  borderRadius: theme.radii.md,
                  color: theme.semantic.text.primary,
                  fontSize: theme.typography.fontSize.base,
                  fontFamily: theme.typography.fontFamily.sans,
                  outline: 'none',
                }}
              />
              <button
                onClick={handleFollowUser}
                style={{
                  background: theme.brand.crimson,
                  color: theme.semantic.text.inverse,
                  border: 'none',
                  borderRadius: theme.radii.md,
                  padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                  fontFamily: theme.typography.fontFamily.sans,
                  fontSize: theme.typography.fontSize.base,
                  fontWeight: theme.typography.fontWeight.bold,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing[2],
                }}
              >
                <UserPlus size={18} />
                Follow
              </button>
            </div>
          </div>

          {/* Followers */}
          <div style={{
            background: theme.semantic.bg.surface,
            border: `1px solid ${theme.semantic.border.default}`,
            borderRadius: theme.radii.md,
            padding: theme.spacing[5],
            marginBottom: theme.spacing[4],
          }}>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.sans,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              margin: 0,
              marginBottom: theme.spacing[3],
            }}>
              Followers
            </h3>

            {followers.length === 0 ? (
              <p style={{
                color: theme.semantic.text.muted,
                fontSize: theme.typography.fontSize.sm,
              }}>
                No followers yet
              </p>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing[2],
              }}>
                {followers.map((follower) => (
                  <div
                    key={follower.username}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: theme.spacing[2],
                      background: theme.brand.charcoal,
                      borderRadius: theme.radii.sm,
                    }}
                  >
                    <span style={{
                      color: theme.semantic.text.primary,
                    }}>
                      {follower.username}
                    </span>
                    {follower.status === 'pending' && (
                      <button
                        onClick={() => handleAcceptFollow(follower.username)}
                        style={{
                          background: theme.brand.crimson,
                          color: theme.semantic.text.inverse,
                          border: 'none',
                          borderRadius: theme.radii.sm,
                          padding: `${theme.spacing[1]} ${theme.spacing[3]}`,
                          fontSize: theme.typography.fontSize.sm,
                          fontWeight: theme.typography.fontWeight.medium,
                          cursor: 'pointer',
                        }}
                      >
                        Accept
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Following */}
          <div style={{
            background: theme.semantic.bg.surface,
            border: `1px solid ${theme.semantic.border.default}`,
            borderRadius: theme.radii.md,
            padding: theme.spacing[5],
          }}>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.sans,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.primary,
              margin: 0,
              marginBottom: theme.spacing[3],
            }}>
              Following
            </h3>

            {following.length === 0 ? (
              <p style={{
                color: theme.semantic.text.muted,
                fontSize: theme.typography.fontSize.sm,
              }}>
                Not following anyone yet
              </p>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing[2],
              }}>
                {following.map((follow) => (
                  <div
                    key={follow.username}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: theme.spacing[2],
                      background: theme.brand.charcoal,
                      borderRadius: theme.radii.sm,
                    }}
                  >
                    <span style={{
                      color: theme.semantic.text.primary,
                    }}>
                      {follow.username}
                    </span>
                    <span style={{
                      color: theme.semantic.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                    }}>
                      {follow.status === 'pending' ? 'Pending' : 'Following'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}